import { useMemo } from "react";
import type { z } from "zod";

import type { Prettify } from "@thalamos/common";
import type { WorkflowVersion } from "@thalamos/vision-common";
import {
  createInitialValues,
  getWorkflowTranslationContext,
  tk,
  workflows,
} from "@thalamos/vision-common";

import type { MultiPageFormProps } from "../../components/FormBuilder/MultiPageForm/MultiPageForm.js";
import {
  MultiPageForm,
  MultiPageFormSkeleton,
} from "../../components/FormBuilder/MultiPageForm/MultiPageForm.js";
import { Error } from "../../pages/Error/Error.js";

type ArrivePlaceOfSafetySchema<T extends WorkflowVersion> = z.infer<
  ReturnType<
    (typeof workflows)[T]["recordPlaceOfSafety"]["formDefinition"]["sections"][1]["buildFormSpecification"]
  >["formSchema"]
>;

///

export function ArrivePlaceOfSafetyFormSkeleton() {
  return <MultiPageFormSkeleton headerText={tk("arrivePlaceOfSafety.title")} />;
}

///

export type ArrivePlaceOfSafetyFormProps<T extends WorkflowVersion> = Prettify<
  {
    workflowVersion: T;
  } & Pick<
    MultiPageFormProps<ArrivePlaceOfSafetySchema<T>>,
    | "initialValues"
    | "incidentWorkItem"
    | "onAutoSave"
    | "onSubmit"
    | "onCancel"
    | "onBack"
  >
>;

///

export function ArrivePlaceOfSafetyForm<T extends WorkflowVersion>(
  props: ArrivePlaceOfSafetyFormProps<T>,
) {
  const workflow = workflows[props.workflowVersion];
  const formSpecification =
    workflow?.recordPlaceOfSafety.formDefinition.sections[1]?.buildFormSpecification(
      { incidentWorkItem: props.incidentWorkItem },
    );

  const initialValuesOrDefaults: Record<string, unknown> = useMemo(
    () =>
      formSpecification === undefined
        ? {}
        : createInitialValues(
            formSpecification.strictFieldSchemas,
            props.initialValues,
          ),
    [props.initialValues, formSpecification],
  );

  if (formSpecification === undefined) {
    return <Error />;
  }

  const formSchema = formSpecification.formSchema;
  const strictFieldSchemas = formSpecification.strictFieldSchemas;

  return (
    <MultiPageForm<typeof formSchema>
      headerText={({ incidentWorkItem }) => ({
        translationKey: tk("arrivePlaceOfSafety.title"),
        translationOptions: {
          context: getWorkflowTranslationContext({ incidentWorkItem }),
        },
      })}
      incidentWorkItem={props.incidentWorkItem}
      schema={formSchema}
      strictFieldSchemas={strictFieldSchemas}
      initialValues={initialValuesOrDefaults}
      pages={formSpecification.pages}
      onBack={props.onBack}
      onCancel={props.onCancel}
      onSubmit={props.onSubmit}
      onAutoSave={props.onAutoSave}
    />
  );
}
